import { useCallback } from 'react';

import { ERROR } from 'components/Toast/constants';

import useToast from 'hooks/useToast';

import { FUNCTION, HTTP_DUPLICATE_STATUS } from './constants';
import type { ApiCallResult, ErrorMessage, ErrorMessageFunction } from './types';

const isErrorMessageFunction = (errorMessage: ErrorMessage): errorMessage is ErrorMessageFunction =>
    typeof errorMessage === FUNCTION;

export const useRetry = () => {
    const { broadcastToast } = useToast();

    const withRetry = useCallback(
        async <T>(
            apiCall: () => Promise<ApiCallResult<T>>,
            errorMessage: ErrorMessage,
            retryFn: () => Promise<T | null>
        ): Promise<T | null> => {
            try {
                const { data, error } = await apiCall();

                if (error) {
                    const message = isErrorMessageFunction(errorMessage)
                        ? errorMessage(error)
                        : errorMessage;

                    const retried = await broadcastToast({
                        text: message,
                        type: ERROR
                    });

                    if (retried) {
                        return retryFn();
                    }

                    return null;
                }

                return data;
            } catch (error) {
                console.error(error);

                const message = isErrorMessageFunction(errorMessage)
                    ? errorMessage(null)
                    : errorMessage;

                const retried = await broadcastToast({
                    text: message,
                    type: ERROR
                });

                if (retried) {
                    return retryFn();
                }

                return null;
            }
        },
        [broadcastToast]
    );

    const withRetryAndDuplicateCheck = useCallback(
        async <T>(
            apiCall: () => Promise<ApiCallResult<T>>,
            errorMessage: string,
            duplicateErrorMessage: string,
            retryFn: () => Promise<T | null>
        ): Promise<T | null> =>
            withRetry(
                apiCall,
                error =>
                    error?.includes(HTTP_DUPLICATE_STATUS) ? duplicateErrorMessage : errorMessage,
                retryFn
            ),
        [withRetry]
    );

    return { withRetry, withRetryAndDuplicateCheck };
};

export default useRetry;
